import React from 'react';
import { EPageViewType, Group, Invoice } from '../../types';
import { FormCard } from '@portal_components/components/FormCard';
import { FormProvider, useForm } from 'react-hook-form';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomerAddressTab } from './CustomerAddressTab';
import { usePortalQuery, EHttpMethod } from '../../hooks/usePortalQuery';
import { FormButton } from '@portal_components/components/FormButton';
import { CustomerContactsTab } from './CustomerContactsTab';
import { CustomerInvoiceTab } from './CustomerInvoiceTab';
import { RelatedGroups } from './RelatedGroups';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormTabHeadings } from '@portal_components/components/FormTabHeadings';
import { FormErrorList } from '@portal_components/components/FormErrorList';
import { useGroupStandingData } from '../../hooks/useGroupStandingData';

interface GroupViewProps {
  viewType: EPageViewType;
  group: Group;
  formSubmitted?: boolean;
  onSubmit?: (values: Group) => Promise<{ success: boolean; message: string }>;
}

enum EGroupTab {
  CUSTOMER_ADDRESS = 'Customer Address',
  CUSTOMER_CONTACT = 'Customer Contact',
  CUSTOMER_INVOICE = 'Customer Invoices'
}

export const invoiceValidationSchema: Yup.AnySchema<Invoice[]> = Yup.array<Invoice>(
  Yup.object<Invoice, { [Key in keyof Invoice]: Yup.AnySchema<Invoice[Key]> }>({
    customerInvoiceName: Yup.string().required('Field is required'),
    defaultInvoiceGroup: Yup.string().oneOf(['Y', 'N']).required(),
    useGroupAddress: Yup.boolean().optional(),
    usePrimaryContact: Yup.boolean().optional(),
    contact: Yup.object().when(['usePrimaryContact'], ([usePrimaryContact], schema) => {
      if (usePrimaryContact) return schema.optional();
      return schema.shape({ groupContactName: Yup.string().required('Field is required') });
    }),
    address: Yup.object().when(['useGroupAddress'], ([useGroupAddress], schema) => {
      if (useGroupAddress) return schema.optional();
      return schema.shape({
        addressName: Yup.string().required('Field is required'),
        addressLine1: Yup.string().required('Field is required'),
        town: Yup.string().required('Field is required'),
        county: Yup.string().required('Field is required'),
        postcode: Yup.string()
          .required('Field is required')
          .matches(/^[a-zA-z]{1,2}[0-9][0-9A-Za-z]?\s?[0-9][a-zA-z]{2}$/, {
            excludeEmptyString: true,
            message: 'Invalid postcode'
          })
      });
    })
  })
) as Yup.AnySchema<Invoice[]>;

const validationSchema = Yup.object<Group, { [Key in keyof Group]: Yup.AnySchema<Group[Key]> }>({
  customerGroupName: Yup.string().required('Field is required'),
  customerGroupTypeId: Yup.string().required('Field is required'),
  companyNumber: Yup.string().required('Field is required'),
  contact: Yup.object({
    groupContactName: Yup.string().required('Field is required')
  }),
  relatedGroup: Yup.object({
    relationshipType: Yup.string().optional(),
    customerRelatedGroupId: Yup.string().when('relationshipType', ([relationshipType], schema) => {
      if (['3', '4', '5'].includes(relationshipType ?? '')) {
        return schema.required('Field is required');
      }
      return schema.optional();
    })
  }),
  address: Yup.object({
    addressName: Yup.string().required('Field is required'),
    addressLine1: Yup.string().required('Field is required'),
    town: Yup.string().required('Field is required'),
    county: Yup.string().required('Field is required'),
    postcode: Yup.string()
      .required('Field is required')
      .matches(/^[a-zA-z]{1,2}[0-9][0-9A-Za-z]?\s?[0-9][a-zA-z]{2}$/, 'Invalid postcode')
  }).required('Field is required'),
  invoices: invoiceValidationSchema
    .test('one-default-invoice', 'There must be exactly 1 default invoice', value => {
      if (!value || value.length === 0) return false;
      const defaultInvoices = value.filter(invoice => invoice.defaultInvoiceGroup === 'Y' && !invoice.markedForDeletion);
      return defaultInvoices.length === 1;
    })
    .required('A default invoice is required')
});

export const GroupView = ({ viewType, group, formSubmitted, onSubmit }: GroupViewProps) => {
  const readonly = viewType === EPageViewType.VIEW;
  const [submitResponse, setSubmitResponse] = React.useState<{ success: boolean; message: string } | null>(null);
  const [currentTab, setCurrentTab] = React.useState<EGroupTab>(EGroupTab.CUSTOMER_ADDRESS);

  const methods = useForm<Group>({
    values: group ?? undefined,
    resolver: yupResolver<Group>(validationSchema as any)
  });

  const { data: customerGroups, isLoading: customerGroupsLoading } = usePortalQuery<{ customerGroupName: string; customerGroupId: string; customerGroupTypeId: string }[]>(
    EHttpMethod.GET,
    'relatable-groups',
    'get-relatable-groups',
    true,
    undefined,
    undefined,
    undefined,
    false,
    600000,
    600000
  );

  const { data: customerContracts, isLoading: customerContractsLoading } = usePortalQuery<{ contractId: string; contractType: string }[]>(
    EHttpMethod.GET,
    `customer-contracts/${group.customerGroupId}`,
    `get-customer-contracts-${group.customerGroupId}`,
    viewType === EPageViewType.EDIT,
    undefined,
    undefined,
    undefined,
    false,
    600000,
    600000
  );

  const {
    isLoading: standingDataLoading,
    standingData: { standingDataGroupTypeList }
  } = useGroupStandingData();

  const handleTabSelected = (tab: EGroupTab) => () => {
    setCurrentTab(tab);
  };

  const handleSubmit = async (values: Group) => {
    setSubmitResponse(null);
    if (!onSubmit) return;
    const res = await onSubmit(values);
    setSubmitResponse(res);
  };

  if ((standingDataLoading || customerGroupsLoading || (viewType === EPageViewType.EDIT && customerContractsLoading)) && !readonly) {
    return <div className="flex flex-row justify-center">Loading customer groups and standing data...</div>;
  }

  return (
    <div className="flex flex-col justify-start">
      <FormProvider {...methods}>
        <form onSubmit={onSubmit && !formSubmitted ? methods.handleSubmit(handleSubmit) : undefined}>
          <FormCard title="Customer Info" columns={2}>
            <FormTextInput<Group> title="Customer Name" name="customerGroupName" readonly={readonly} mandatory />
            <FormDropdown<Group>
              title="Customer Group Type Id"
              name="customerGroupTypeId"
              readonly={readonly}
              mandatory
              placeholder="Select Customer Type"
              elementsForDisplay={standingDataGroupTypeList.map(groupType => ({
                title: groupType.customerGroupTypeDesc,
                value: groupType.customerGroupTypeId
              }))}
            />
            <FormTextInput<Group> title="Companies House Registration Number" name="companyNumber" readonly={readonly} mandatory />
            <FormTextInput<Group> title="SAP Customer Number" name="sapCustomerNumber" readonly={readonly} />
          </FormCard>
          <RelatedGroups readonly={readonly} customerGroups={customerGroups ?? []} />
          <FormTabHeadings<EGroupTab> tabs={Object.values(EGroupTab)} currentTab={currentTab} handleTabSelected={handleTabSelected} />
          {currentTab === EGroupTab.CUSTOMER_ADDRESS && <CustomerAddressTab readonly={readonly} />}
          {currentTab === EGroupTab.CUSTOMER_INVOICE && <CustomerInvoiceTab readonly={readonly} customerContracts={customerContracts} viewType={viewType} />}
          {currentTab === EGroupTab.CUSTOMER_CONTACT && <CustomerContactsTab readonly={readonly} />}
          <FormErrorList />
          {submitResponse && <div className={`pb-2 text-sm ${submitResponse.success ? 'text-feedback-green' : 'text-feedback-red'}`}>{submitResponse.message}</div>}
          {!readonly && (
            <FormButton type="submit" disabled={formSubmitted} primary>
              Submit
            </FormButton>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
